import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { getTrans as t } from "../../Localization/translations";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import AddcategoryService from "../../services/category/categoryAddServices";

export const toastOptions = {
    position: "top-right",
    autoClose: 1000,
    hideProgressBar: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
};

export const errorMessage = (error) =>
    error?.response?.data?.error || error?.response?.data?.detail || "Something went wrong.";

// Same look as the expanded row on the Accounts page: light grey panel, small uppercase headings.
export const panelStyle = { background: "#FAFAFA", padding: "10px 16px 6px", boxSizing: "border-box", maxWidth: "100%", overflow: "hidden" };
const gridStyle = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 62px", columnGap: 10, alignItems: "center" };
export const controlStyle = { fontSize: 12, height: 28, padding: "2px 8px", minWidth: 0 };
export const buttonStyle = { fontSize: 11, lineHeight: 1, height: 26, width: 26, padding: 0 };
export const headingStyle = {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#6B7280",
};
export const columns = { minWidth: 0 };

let draftCounter = 0;
const toRow = (code) => ({ key: `saved-${code.id}`, ...code, dirty: false });
const newDraft = () => ({
    key: `draft-${++draftCounter}`,
    id: null,
    category_id: "",
    sub_category_name: "",
    tax_code: "",
    dirty: false,
});
// Saved rows, then always one blank row to fill in.
const withBlankRow = (rows) => {
    const last = rows[rows.length - 1];
    return last && !last.id && !last.dirty ? rows : [...rows, newDraft()];
};

// Opens under a product's Rent line on Settings > Products: the Avalara tax code a Rent line takes
// for assets of an Item Category / Item Sub Category. Item Category and Item Sub Category come from
// the Inventory Categories API. Used only when Avalara is the tax engine.
// A blank sub category means the whole category; a sub category row wins over it.
const RentMapping = ({ product, codes, onRowsChange }) => {
    const [rows, setRows] = useState(() => withBlankRow(codes.map(toRow)));
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState({});   // {categoryId: [sub category names]}

    useEffect(() => {
        AddcategoryService.viewListCategory(100)
            .then((response) => setCategories(response.data.data.categories || []))
            .catch(() => toast.error(t("Could not load the inventory categories."), toastOptions));
    }, []);

    // Sub categories of every category in use: saved rows on open, newly picked ones as they are chosen.
    useEffect(() => {
        const wanted = [...new Set(rows.map((row) => String(row.category_id)).filter(Boolean))];
        wanted.forEach((categoryId) => {
            if (subCategories[categoryId] !== undefined) return;
            const category = categories.find((c) => String(c.id) === categoryId);
            if (!category) return;
            setSubCategories((prev) => ({ ...prev, [categoryId]: [] }));   // mark as requested
            AddcategoryService.filtersubCategory(category.category_name)
                .then((response) =>
                    setSubCategories((prev) => ({
                        ...prev,
                        [categoryId]: (response.data.sub_categories || []).map((sub) => sub.sub_category_name),
                    }))
                )
                .catch(() => toast.error(t("Could not load the sub categories."), toastOptions));
        });
    }, [rows, categories, subCategories]);

    // Tell the page which rows are saved, so reopening the panel shows them.
    const publish = (next) => {
        setRows(withBlankRow(next));
        onRowsChange(
            product.id,
            next.filter((row) => row.id).map(({ id, product_setting_id, category_id, sub_category_name, tax_code }) => ({
                id, product_setting_id, category_id, sub_category_name, tax_code,
            }))
        );
    };

    const update = (key, patch) =>
        setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch, dirty: true } : row)));

    const subCategoriesOf = (categoryId) => subCategories[String(categoryId)] || [];

    const save = (row) => {
        const body = {
            product_setting_id: product.id,
            category_id: row.category_id,
            sub_category_name: row.sub_category_name,
            tax_code: (row.tax_code || "").trim(),
        };
        const request = row.id
            ? axios.put(API + `company/product-tax-category-codes/${row.id}/`, body, { headers: authHeader() })
            : axios.post(API + "company/product-tax-category-codes/", body, { headers: authHeader() });
        request
            .then((response) => {
                const saved = toRow(response.data.data);
                // A save over an existing category / sub category pair returns that row: don't show it twice.
                publish(
                    rows
                        .filter((r) => r.key === row.key || r.id !== saved.id)
                        .map((r) => (r.key === row.key ? saved : r))
                );
                toast.success(t("Tax code updated successfully."), toastOptions);
            })
            .catch((error) => toast.error(errorMessage(error), toastOptions));
    };

    // x on a saved row deletes it; on a row being filled in, it clears it.
    const remove = (row) => {
        if (!row.id) {
            setRows((prev) => withBlankRow(prev.filter((r) => r.key !== row.key)));
            return;
        }
        axios
            .delete(API + `company/product-tax-category-codes/${row.id}/`, { headers: authHeader() })
            .then(() => {
                publish(rows.filter((r) => r.key !== row.key));
                toast.success(t("Tax code removed."), toastOptions);
            })
            .catch((error) => toast.error(errorMessage(error), toastOptions));
    };

    return (
        <div style={panelStyle}>
            <div className="mb-1" style={gridStyle}>
                <div style={{ ...columns, ...headingStyle }}>{t("Item Category")}</div>
                <div style={{ ...columns, ...headingStyle }}>{t("Item Sub Category")}</div>
                <div style={{ ...columns, ...headingStyle }}>{t("Tax Code")}</div>
                <div></div>
            </div>

            {rows.map((row) => {
                const complete = row.category_id && (row.tax_code || "").trim();
                const touched = row.id || row.dirty;
                return (
                    <div key={row.key} className="mb-1" style={gridStyle}>
                        <div style={columns}>
                            <select
                                className="form-select form-select-sm" style={controlStyle}
                                value={row.category_id}
                                onChange={(e) => update(row.key, { category_id: e.target.value, sub_category_name: "" })}
                            >
                                <option value="">{t("Select Category")}</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.category_name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={columns}>
                            <select
                                className="form-select form-select-sm" style={controlStyle}
                                value={row.sub_category_name}
                                disabled={!row.category_id}
                                onChange={(e) => update(row.key, { sub_category_name: e.target.value })}
                            >
                                <option value="">{t("All sub categories")}</option>
                                {subCategoriesOf(row.category_id).map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={columns}>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                style={controlStyle}
                                maxLength={30}
                                value={row.tax_code}
                                onChange={(e) => update(row.key, { tax_code: e.target.value })}
                            />
                        </div>
                        <div className="text-nowrap d-flex" style={{ gap: 4 }}>
                            {row.dirty && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    style={buttonStyle}
                                    disabled={!complete}
                                    title={t("Save")}
                                    onClick={() => save(row)}
                                >
                                    &#10003;
                                </button>
                            )}
                            {touched && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    style={buttonStyle}
                                    title={row.id ? t("Delete") : t("Clear")}
                                    onClick={() => remove(row)}
                                >
                                    x
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default RentMapping;
